import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Permission, Role } from '@shared/roles';
import { ROLE_LABELS } from '@shared/roles';
import { api } from './api';
import { homeFor, useSession } from './session';
import { BrandLogo, Icon, greeting, type IconName } from './brand';
import { ThemeToggle } from './theme';
import type { IndentView } from './types';

interface NavItem {
  to: string;
  label: string;
  /** Shorter label for the phone's bottom bar. */
  tab?: string;
  icon: IconName;
  perms?: Permission[];
  roles?: Role[];
}

const NAV: NavItem[] = [
  { to: '/buyer', label: 'Home', tab: 'Home', icon: 'home', roles: ['BUYER_ADMIN', 'BUYER_APPROVER', 'BUYER_ACCOUNTS'] },
  { to: '/buyer/indents', label: 'Indents', tab: 'Indents', icon: 'ticket', perms: ['indent.view.all'] },
  { to: '/buyer/suppliers', label: 'Bills', tab: 'Bills', icon: 'bills', perms: ['buyer.statements.view'] },
  { to: '/buyer/people', label: 'Drivers & vehicles', icon: 'people', perms: ['buyer.people.view', 'buyer.people.manage'] },
  { to: '/buyer/payments', label: 'Payments', icon: 'wallet', perms: ['buyer.payments.report'] },
  { to: '/buyer/policy', label: 'Rules & limits', icon: 'rules', perms: ['buyer.policy.manage'] },
  { to: '/supplier', label: 'Home', tab: 'Home', icon: 'home', perms: ['supplier.ledger.view'] },
  { to: '/supplier/customers', label: 'Customers', tab: 'Customers', icon: 'store', perms: ['supplier.ledger.view', 'supplier.relationships.manage'] },
  { to: '/supplier/payments', label: 'Payments', tab: 'Payments', icon: 'wallet', perms: ['supplier.payments.confirm', 'supplier.payments.record'] },
  { to: '/station', label: 'Fill fuel', tab: 'Fill fuel', icon: 'fuel', perms: ['redeem.validate', 'supplier.staff.manage'] },
  { to: '/supplier/claims', label: 'Finish fuel entries', tab: 'Finish entries', icon: 'clock', perms: ['supplier.claims.resolve'] },
  { to: '/supplier/setup', label: 'Pump setup', icon: 'wrench', perms: ['supplier.locations.manage', 'supplier.prices.manage', 'supplier.staff.manage'] },
  { to: '/supplier/accounting', label: 'Accounting', icon: 'book', perms: ['supplier.ledger.view'] },
  { to: '/adjustments', label: 'Corrections', icon: 'edit', perms: ['supplier.adjustments.propose', 'buyer.adjustments.approve'] },
  { to: '/disputes', label: 'Disputes', icon: 'alert', perms: ['buyer.disputes.raise', 'supplier.disputes.handle'] },
  { to: '/reports', label: 'Reports', icon: 'chart', perms: ['reports.view'] },
  { to: '/org/profile', label: 'Business details', icon: 'store', perms: ['org.members.manage'] },
  { to: '/audit', label: 'History', icon: 'history', perms: ['audit.view'] },
  { to: '/platform', label: 'Support', tab: 'Support', icon: 'help', perms: ['platform.support'] },
];

const MAX_TABS = 4;

export function useNavItems() {
  const { can, has } = useSession();
  const items = NAV.filter((n) => (!n.perms || can(...n.perms)) && (!n.roles || has(...n.roles)));
  const tabs = items.filter((n) => n.tab).slice(0, MAX_TABS);
  const rest = items.filter((n) => !tabs.includes(n));
  return { items, tabs, rest };
}

function usePendingApprovals(enabled: boolean) {
  const q = useQuery({ queryKey: ['approvals'], queryFn: () => api.get<IndentView[]>('/api/approvals/queue'), enabled, staleTime: 30_000, refetchInterval: 60_000 });
  return enabled ? q.data?.length ?? 0 : 0;
}

export function Layout() {
  const { me, active, choose, can } = useSession();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { items, tabs } = useNavItems();
  const pending = usePendingApprovals(can('indent.approve'));
  const business = me?.memberships.filter((m) => !me.station || m.orgId === me.station.orgId) ?? [];
  const firstName = me?.user.name.split(' ')[0];

  return (
    <div className="app">
      <header className="topbar">
        <NavLink to={homeFor(active)} className="brand-link" aria-label="SuperIndent home"><BrandLogo /><span>SuperIndent</span></NavLink>
        <div className="who">
          <span className="hello">{me?.station ? me.station.locationName ?? 'Fill fuel' : `${greeting()}, ${firstName}`}</span>
          {business.length > 1 && !me?.station ? (
            <select
              className="org-switch"
              aria-label="Business and role"
              value={active?.orgId ?? ''}
              onChange={(e) => {
                choose(e.target.value);
                const m = business.find((b) => b.orgId === e.target.value) ?? null;
                nav(homeFor(m));
              }}
            >
              {!active ? <option value="">Choose business…</option> : null}
              {business.map((m) => (
                <option key={m.orgId} value={m.orgId}>
                  {m.orgName} · {m.roles.map((r) => ROLE_LABELS[r].split(' (')[0]).join(', ')}
                </option>
              ))}
            </select>
          ) : (
            <span className="org-name">{me?.station ? (me.signedInAs ? me.user.name : 'Shared pump device') : active?.orgName}</span>
          )}
        </div>
        <ThemeToggle />
        <button className="bell" aria-label={`Notifications${me?.unreadNotifications ? `: ${me.unreadNotifications} new` : ''}`} onClick={() => nav('/notifications')}>
          <Icon name="bell" size={24} />
          {me?.unreadNotifications ? <span className="dot">{me.unreadNotifications}</span> : null}
        </button>
      </header>
      <div className="layout">
        <nav className="sidenav" aria-label="Main">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} end>
              <Icon name={n.icon} />
              <span>{n.label}</span>
              {n.to === '/buyer/approvals' && pending ? <span className="count">{pending}</span> : null}
            </NavLink>
          ))}
          <hr />
          <NavLink to="/notifications"><Icon name="bell" /><span>Notifications</span></NavLink>
          <NavLink to="/account"><Icon name="user" /><span>My account</span></NavLink>
          <SignOutLink />
          <p className="side-note">{me?.user.name}</p>
        </nav>
        <main>
          {['/supplier', '/buyer', '/driver'].includes(pathname) ? <p className="home-tagline">Fuel indents made simple</p> : null}
          <Outlet />
        </main>
      </div>
      <nav className="bottomnav" aria-label="Main">
        {tabs.map((n) => (
          <NavLink key={n.to} to={n.to} end>
            <span className="tab-icon">
              <Icon name={n.icon} size={24} />
              {n.to === '/buyer/approvals' && pending ? <span className="count">{pending}</span> : null}
            </span>
            <span>{n.tab}</span>
          </NavLink>
        ))}
        <NavLink to="/more" end>
          <span className="tab-icon"><Icon name="more" size={24} /></span>
          <span>More</span>
        </NavLink>
      </nav>
    </div>
  );
}

function SignOutLink({ menu }: { menu?: boolean }) {
  const { logout } = useSession();
  return (
    <a href="#logout" onClick={(e) => { e.preventDefault(); void logout(); }}>
      {menu ? <span className="menu-icon"><Icon name="logout" /></span> : <Icon name="logout" />}
      <span>Sign out</span>
    </a>
  );
}

/** Phone "More" menu: everything that is not on the bottom bar. */
export function MorePage() {
  const { me } = useSession();
  const { rest } = useNavItems();
  return (
    <div className="stack">
      <h1>More</h1>
      <nav className="menu-list" aria-label="More">
        {rest.map((n) => (
          <NavLink key={n.to} to={n.to} end>
            <span className="menu-icon"><Icon name={n.icon} /></span>
            <span>{n.label}</span>
            <Icon name="next" size={18} className="chev" />
          </NavLink>
        ))}
        <NavLink to="/notifications"><span className="menu-icon"><Icon name="bell" /></span><span>Notifications</span><Icon name="next" size={18} className="chev" /></NavLink>
        <NavLink to="/account"><span className="menu-icon"><Icon name="user" /></span><span>My account</span><Icon name="next" size={18} className="chev" /></NavLink>
        <SignOutLink menu />
      </nav>
      <p className="muted" style={{ textAlign: 'center' }}>Signed in as {me?.user.name}</p>
      <p className="footer-note">
        SuperIndent keeps records between your business and your supplier. The supplier gives the credit; SuperIndent does not lend, guarantee payment or hold money.
      </p>
    </div>
  );
}
